import axios from 'axios';
import React, { useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { useContextSelector } from 'use-context-selector';
import _ from 'lodash';

import styles from './LicenseCheck.module.scss';
import {
  LicenseContext,
  VerifiedLicense,
} from '../../context/License/LicenseContext';
import Icon from '../Icon/Icon';
import Button from '../Button/Button';
import Label from '../../context/Label/Label';

type LicenseCheckProps = {
  globalState: any;
};

type DeniedCode = 'expired' | 'inactive' | 'not-found' | 'check-failed';

function LicenseCheck({ globalState = {} }: LicenseCheckProps) {
  const { verifiedLicense, setVerifiedLicense } = useContextSelector(
    LicenseContext,
    (c) => c
  );
  const { course = {}, scorm = {} } = globalState;
  const { license = {} } = course;
  const { studentId, studentName } = scorm.initialScormData;
  const { id, serviceUrl } = license;
  const [deniedCode, setDeniedCode] = useState<DeniedCode>();
  const isJustAWarning = deniedCode === 'check-failed';

  const messages = {
    expired: {
      title: 'License expired',
      message: 'The course cannot be accessed due to an expired license.',
      callToAction: 'Contact the vendor to renew.',
    },
    inactive: {
      title: 'License inactive',
      message: 'The course cannot be accessed due to an inactive license.',
      callToAction: 'Contact the vendor to reactivate.',
    },
    'not-found': {
      title: 'License not found',
      message:
        'The course cannot be accessed due to the license not being found.',
      callToAction: 'Contact the vendor to verify this course license.',
    },
    'check-failed': {
      title: 'License could not be verified',
      message: 'An error occurred while checking this license.',
      callToAction: 'Contact the vendor to avoid this message.',
    },
  };

  function getCourseId(course = {}) {
    // @ts-ignore
    return course.courseId || course.id || course.docId;
  }

  useEffect(() => {
    if (!id || !serviceUrl) return;

    fetchLicense();

    async function fetchLicense() {
      try {
        const { data } = await axios.post<VerifiedLicense>(serviceUrl, {
          origin: document?.location?.origin,
          studentId,
          studentName,
          courseId: getCourseId(course),
          courseName: course.name,
        });
        setVerifiedLicense(data);
        if (data.denied && data.licenseStatus !== 'active')
          setDeniedCode(data.licenseStatus);
      } catch (err) {
        console.error(err);
        if (_.get(err, 'response.status') === 404) {
          setDeniedCode('not-found');
        } else {
          setDeniedCode('check-failed');
        }
      }
    }
  }, []); // eslint-disable-line

  if (deniedCode)
    return (
      <ReactModal
        isOpen={true}
        className={styles.content}
        overlayClassName={styles.overlay}
        portalClassName={styles.portal}
        ariaHideApp={false}
      >
        <div className={styles.messageContainer} data-testid="LicenseCheck">
          {isJustAWarning ? (
            <Icon icon="exclamation" className={styles.warningIcon} />
          ) : (
            <Icon icon="times" className={styles.dangerIcon} />
          )}
          <p>
            <Label>{messages[deniedCode].title}</Label>
          </p>
          <span>
            <Label>{messages[deniedCode].message}</Label>
          </span>
          <span>
            <Label>{messages[deniedCode].callToAction}</Label>
          </span>
          <hr />
          <span className={styles.licenseInfo}>
            {course?.name} ({getCourseId(course)})
          </span>
          {verifiedLicense && (
            <span className={styles.licenseInfo}>
              Licensed to: {verifiedLicense.licensedTo}
            </span>
          )}
          <span className={styles.licenseInfo}>
            License id: {course?.license?.id}
          </span>
          {isJustAWarning && (
            <Button
              className={styles.okButton}
              onClick={() => setDeniedCode(undefined)}
            >
              ok
            </Button>
          )}
        </div>
      </ReactModal>
    );

  return null;
}

export default LicenseCheck;
